// libraries
import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import RichText from '/react4xp/components/RichText'
import Checkbox from '/react4xp/components/Form/Simple/Checkbox'
import MaritimePartnerCard from '/react4xp/components/MaritimePartnerCard'
import Announcer, { useAnnouncer, fillPlaceholders } from '/react4xp/components/Announcer'

// svg
import { AngleDown } from '/react4xp/components/Svg/Ux/Angle'
import { Expand, Contract, Close } from '/react4xp/components/Svg/Ux/Ux'
import { UxArrowLeft, UxArrowRight } from '../Svg/Ux/Arrow'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils'

// Only used when no partner pin has a location to centre on.
const DEFAULT_MAP_CENTER = { lat: 60.2054911, lng: 24.6559001 }

export interface MaritimePartnersPageProps {
	resultsPerPage: number
	mapPlaceholder: string
	topPart: {
		title: string
		intro: string
	}
	searchPlaceholder: string
	operations?: {
		title: string,
		items: Array<{
			title: string
		}>
	}
	productType?: {
		title: string,
		items: Array<{
			title: string
		}>
	}
	partners: Array<{
		id: string
		companyName: string
		location: {
			country: string
			city: string
			fullAddress: string
		},
		phones?: Array<{
			phone: string
		}>
		emails?: Array<{
			email: string
			sector?: string
		}>
		website?: string
		operations: Array<string>
		productTypes?: Array<string>
	}>
	totalPartners?: number
	partnersEndpoint?: string
	allPartnersUrl?: string
	maps?: {
		pins: Array<{
			id: string
			name: string
            address: string
            location: {
                lat: number
                lng: number
            }
		}>
		mapId: string
	}
	tabsText: {
		headerText: string
		listText: string
		mapText: string
	}
	noResultsMessage?: string
	resultsStatus?: string
	loading?: string
}

export default function MaritimePartnersPage({
	resultsPerPage,
	mapPlaceholder,
	topPart,
	searchPlaceholder,
	operations,
	partners,
	totalPartners,
	partnersEndpoint,
	allPartnersUrl,
	maps,
	tabsText,
	productType,
	noResultsMessage = 'No results found',
	resultsStatus = 'Partners: showing <<shown>> of <<total>>',
	loading = 'Loading...',
}: MaritimePartnersPageProps) {

	// add body class
	useEffect(() => {
		document.body.classList.add('maritime-partners')
	}, [])

	const [isListMap, setIsListMap] = useState<'list' | 'map'>('list')
	const [operationsFilter, setOperationsFilter] = useState(false)
	const [productTypeFilter, setProductTypeFilter] = useState(false)
	const [fakeMap, setFakeMap] = useState(false)
	const [fakeFullscreenMap, setFakeFullscreenMap] = useState(false)
	const [isMapOpen, setIsMapOpen] = useState(false)
	const [activePartnerMap, setActivePartnerMap] = useState(null)
	const [showPinInfo, setShowPinInfo] = useState(false)
	// Only the first page of partners (and no map pins) arrives in the initial
	// props; the rest is fetched from partnersEndpoint the first time a search,
	// a filter, the map or "load more" needs it. See JOT-266.
	const hasFullListUpFront = !partnersEndpoint || (totalPartners == null) || (partners.length >= totalPartners)
	const [isFullListLoaded, setIsFullListLoaded] = useState(hasFullListUpFront)
	const allPartnersRef = useRef(partners)
	const allPinsRef = useRef(maps?.pins || [])
	const isFullListLoadedRef = useRef(hasFullListUpFront)
	const fullListRequestRef = useRef(null)

	// Resolves with the complete partner list, fetching it at most once.
	// Resolves with whatever is already in hand when there is no endpoint or the
	// request fails, so list callers can stay on the happy path; the map checks
	// isFullListLoadedRef because it has no pins without the full response.
	const loadFullList = (): Promise<any[]> => {
		if (isFullListLoadedRef.current || !partnersEndpoint) {
			return Promise.resolve(allPartnersRef.current)
		}

		if (!fullListRequestRef.current) {
			fullListRequestRef.current = fetch(partnersEndpoint)
				.then(response => {
					if (!response.ok) {
						throw new Error(`Partners request failed with status ${response.status}`)
					}
					return response.json()
				})
				.then(data => {
					const loadedPartners = data?.partners || []
					const loadedPins = data?.pins || []

					allPartnersRef.current = loadedPartners
					allPinsRef.current = loadedPins
					isFullListLoadedRef.current = true

					setIsFullListLoaded(true)

					return loadedPartners
				})
				.catch(error => {
					console.error('Could not load the full partner list', error)
					fullListRequestRef.current = null
					return allPartnersRef.current
				})
		}

		return fullListRequestRef.current
	}

	const [filteredPartners, setFilteredPartners] = useState(partners)
	const [filteredPins, setFilteredPins] = useState(maps?.pins || [])
	const [isMapAlreadyOpened, setIsMapAlreadyOpened] = useState(false)
	const [hasMapLoadError, setHasMapLoadError] = useState(false)
	const [isParamsInitialized, setIsParamsInitialized] = useState(false)
	const [mapInstance, setMapInstance] = useState(null)
    const [fullMapInstance, setFullMapInstance] = useState(null)
    // On ?all=1 the server sends every partner on purpose, so every card is
    // rendered server-side and the crawlable full-list address really is one.
    // Without an endpoint (the showcase) the list pages client-side as before.
    const isFullListRequested = !!partnersEndpoint && totalPartners != null && partners.length >= totalPartners
    const [displayedItems, setDisplayedItems] = useState(isFullListRequested ? partners.length : resultsPerPage)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    // state lags a render behind, so a held Space could load the same page twice
    const isLoadingMoreRef = useRef(false)
    const itemsPerPage = resultsPerPage || 9

	const [mapMarkerClusterer, setMapMarkerClusterer] = useState(null)
	const [mapMarkerClustererObjs, setMapMarkerClustererObjs] = useState([])
	const [fullMapMarkerClusterer, setFullMapMarkerClusterer] = useState(null)
	const [fullMapMarkerClustererObjs, setFullMapMarkerClustererObjs] = useState([])

	const filteredPinsRef = useRef([])
	const contentRef = useRef<HTMLDivElement>(null)
	const listRef = useRef<HTMLDivElement>(null)
	const focusIndexRef = useRef<number | null>(null)
	const [announcement, announce] = useAnnouncer()

	const announceResults = (list: any[], shown: number) => {
		if (!list.length) return announce(noResultsMessage)
		announce(fillPlaceholders(resultsStatus, { shown: shown, total: list.length }))
	}

	// a filter resets to the first page
	const announceFilter = (list: any[]) => announceResults(list, Math.min(itemsPerPage, list.length))

	const defaultIcon = '<svg width="39" height="47" viewBox="0 0 39 47" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M37.4805 18.3556C37.4805 33.9756 19.3535 46.1246 19.3535 46.1246C19.3535 46.1246 1.22656 33.9756 1.22656 18.3556C1.22656 13.7526 3.13636 9.33814 6.53583 6.08334C9.93529 2.82853 14.546 1 19.3535 1C24.1611 1 28.7718 2.82853 32.1712 6.08334C35.5707 9.33814 37.4805 13.7526 37.4805 18.3556Z" fill="white" stroke="#7DB5FF" stroke-width="1.73556" stroke-linecap="round" stroke-linejoin="round"/><path d="M10.4128 22.539C11.7526 24.4648 13.7544 25.8941 16.0841 26.4917C16.1062 26.4966 16.1271 26.5016 16.1492 26.5077C16.2057 26.5225 16.2634 26.5348 16.3211 26.5484C16.3813 26.5619 16.4415 26.5755 16.5029 26.589C16.5139 26.5915 16.5238 26.5927 16.5336 26.5952C17.1746 26.7258 17.839 26.7948 18.5182 26.7948C20.9375 26.7948 23.1579 25.9212 24.8833 24.4734C24.9005 24.4562 24.9128 24.4353 24.9018 24.4143C24.8956 24.4044 24.8821 24.3958 24.8649 24.3983C24.8575 24.3983 24.8465 24.4032 24.8305 24.4081C24.1588 24.6657 23.3249 24.8 22.3719 24.8C21.6498 24.8 20.8601 24.7236 20.0226 24.5683C19.9943 24.5634 19.9649 24.556 19.9354 24.5511C19.874 24.5412 19.8126 24.5301 19.7512 24.519C16.8725 23.9868 13.8674 22.6215 11.6986 20.8633L10.4128 22.539ZM10.3809 16.8662C12.4195 19.3391 16.6502 21.6617 20.8061 22.4318C24.1453 23.0503 26.5892 22.5094 27.4476 21.1923C27.8676 20.331 28.166 19.4007 28.3244 18.4236C28.3306 18.3731 28.3244 18.3559 28.2986 18.3472C28.2815 18.3411 28.2581 18.3534 28.2446 18.3805C28.2397 18.3904 28.2348 18.4002 28.2286 18.4101C28.2151 18.436 28.2016 18.4606 28.1881 18.4852C28.1795 18.5 28.1709 18.5173 28.1599 18.5358C28.1537 18.5468 28.1451 18.5592 28.1378 18.5715C27.3285 19.8628 24.9644 20.3963 21.7235 19.7962C17.9127 19.0902 14.0381 17.0301 11.9798 14.7802L10.3809 16.8674V16.8662ZM14.7541 14.0027C16.7043 15.5527 19.508 16.8613 22.2749 17.3738C26.0611 18.0762 28.5148 17.0535 28.2962 15.0562C28.2422 14.7568 28.1746 14.4623 28.0948 14.1727C28.085 14.137 28.0751 14.1013 28.0653 14.0692C27.9265 13.5838 27.7509 13.1131 27.5421 12.6609C27.5176 12.6116 27.493 12.5611 27.4685 12.5118C27.4611 12.497 27.455 12.4835 27.4488 12.4699C27.4353 12.4416 27.4046 12.3886 27.3714 12.4022C27.3592 12.4071 27.3432 12.4231 27.3506 12.4551C27.3518 12.4601 27.3542 12.4712 27.3579 12.4872C27.7816 14.2467 25.786 15.1794 22.6138 14.5917C20.332 14.169 18.0183 13.0663 16.4623 11.7738L14.7528 14.0027H14.7541ZM23.4059 8.18206C23.3924 8.1833 23.3802 8.18822 23.3691 8.19808C23.3298 8.23012 23.3421 8.28433 23.3752 8.33239C23.4538 8.44821 24.047 9.12835 23.8456 9.62121C23.8284 9.66556 23.8051 9.70622 23.7756 9.74319C23.7744 9.74565 23.7731 9.74812 23.7707 9.74935C23.7707 9.74935 23.7695 9.74935 23.7682 9.75058C23.557 10.0044 23.0437 10.103 22.3572 9.97606C21.9531 9.9009 21.5466 9.75797 21.1807 9.57562C21.1622 9.56699 21.1475 9.5596 21.1475 9.5596C21.0493 9.50785 20.7754 9.35383 20.671 9.35383C20.531 9.35383 20.3873 9.5214 20.2927 9.64708C20.2891 9.65201 20.2842 9.65694 20.2792 9.6631C20.2485 9.70253 20.2178 9.74319 20.1884 9.78385C19.9501 10.0993 19.5854 10.5749 19.2734 10.9815C20.262 11.5803 21.457 12.0584 22.6409 12.2765C25.0737 12.7274 26.5339 11.9192 25.9014 10.4689C25.727 10.0685 25.4102 9.66187 24.9926 9.27251C24.5444 8.88439 24.0605 8.53692 23.5484 8.23381C23.5042 8.20794 23.4551 8.18083 23.4145 8.18083C23.4109 8.18083 23.4084 8.18083 23.4047 8.18083" fill="#002855"/></svg>'

	const activeIcon = '<svg width="39" height="47" viewBox="0 0 39 47" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M37.4805 18.3556C37.4805 33.9756 19.3535 46.1246 19.3535 46.1246C19.3535 46.1246 1.22656 33.9756 1.22656 18.3556C1.22656 13.7526 3.13636 9.33814 6.53583 6.08334C9.93529 2.82853 14.546 1 19.3535 1C24.1611 1 28.7718 2.82853 32.1712 6.08334C35.5707 9.33814 37.4805 13.7526 37.4805 18.3556Z" fill="#DA4323" stroke="#DA4323" stroke-width="1.73556" stroke-linecap="round" stroke-linejoin="round"/><path d="M10.4128 22.539C11.7526 24.4649 13.7544 25.8942 16.0841 26.4918C16.1062 26.4967 16.1271 26.5016 16.1492 26.5078C16.2057 26.5226 16.2634 26.5349 16.3211 26.5484C16.3813 26.562 16.4415 26.5755 16.5029 26.5891C16.5139 26.5916 16.5238 26.5928 16.5336 26.5953C17.1746 26.7259 17.839 26.7949 18.5182 26.7949C20.9375 26.7949 23.1579 25.9213 24.8833 24.4735C24.9005 24.4563 24.9128 24.4353 24.9018 24.4144C24.8956 24.4045 24.8821 24.3959 24.8649 24.3983C24.8575 24.3983 24.8465 24.4033 24.8305 24.4082C24.1588 24.6657 23.3249 24.8 22.3719 24.8C21.6498 24.8 20.8601 24.7236 20.0226 24.5684C19.9943 24.5635 19.9649 24.5561 19.9354 24.5511C19.874 24.5413 19.8126 24.5302 19.7512 24.5191C16.8725 23.9868 13.8674 22.6216 11.6986 20.8633L10.4128 22.539ZM10.3809 16.8663C12.4195 19.3392 16.6502 21.6618 20.8061 22.4319C24.1453 23.0504 26.5892 22.5095 27.4476 21.1923C27.8676 20.331 28.166 19.4008 28.3244 18.4237C28.3306 18.3732 28.3244 18.3559 28.2986 18.3473C28.2815 18.3411 28.2581 18.3535 28.2446 18.3806C28.2397 18.3904 28.2348 18.4003 28.2286 18.4101C28.2151 18.436 28.2016 18.4607 28.1881 18.4853C28.1795 18.5001 28.1709 18.5173 28.1599 18.5358C28.1537 18.5469 28.1451 18.5592 28.1378 18.5715C27.3285 19.8628 24.9644 20.3964 21.7235 19.7963C17.9127 19.0903 14.0381 17.0301 11.9798 14.7802L10.3809 16.8675V16.8663ZM14.7541 14.0028C16.7043 15.5528 19.508 16.8613 22.2749 17.3739C26.0611 18.0762 28.5148 17.0535 28.2962 15.0562C28.2422 14.7568 28.1746 14.4624 28.0948 14.1728C28.085 14.1371 28.0751 14.1013 28.0653 14.0693C27.9265 13.5838 27.7509 13.1132 27.5421 12.661C27.5176 12.6117 27.493 12.5612 27.4685 12.5119C27.4611 12.4971 27.455 12.4835 27.4488 12.47C27.4353 12.4416 27.4046 12.3887 27.3714 12.4022C27.3592 12.4071 27.3432 12.4232 27.3506 12.4552C27.3518 12.4601 27.3542 12.4712 27.3579 12.4872C27.7816 14.2467 25.786 15.1795 22.6138 14.5917C20.332 14.1691 18.0183 13.0663 16.4623 11.7738L14.7528 14.0028H14.7541ZM23.4059 8.18213C23.3924 8.18336 23.3802 8.18829 23.3691 8.19814C23.3298 8.23018 23.3421 8.28439 23.3752 8.33245C23.4538 8.44827 24.047 9.12841 23.8456 9.62127C23.8284 9.66562 23.8051 9.70628 23.7756 9.74325C23.7744 9.74571 23.7731 9.74818 23.7707 9.74941C23.7707 9.74941 23.7695 9.74941 23.7682 9.75064C23.557 10.0045 23.0437 10.103 22.3572 9.97612C21.9531 9.90096 21.5466 9.75804 21.1807 9.57568C21.1622 9.56705 21.1475 9.55966 21.1475 9.55966C21.0493 9.50791 20.7754 9.35389 20.671 9.35389C20.531 9.35389 20.3873 9.52146 20.2927 9.64714C20.2891 9.65207 20.2842 9.657 20.2792 9.66316C20.2485 9.70259 20.2178 9.74325 20.1884 9.78391C19.9501 10.0993 19.5854 10.5749 19.2734 10.9816C20.262 11.5804 21.457 12.0584 22.6409 12.2765C25.0737 12.7275 26.5339 11.9192 25.9014 10.469C25.727 10.0685 25.4102 9.66193 24.9926 9.27257C24.5444 8.88445 24.0605 8.53698 23.5484 8.23388C23.5042 8.208 23.4551 8.18089 23.4145 8.18089C23.4109 8.18089 23.4084 8.18089 23.4047 8.18089" fill="white"/></svg>'

	const clusterIcon = '<svg width="65" height="64" viewBox="0 0 65 64" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="32.2266" cy="32" r="32" fill="#A3CBFF" fill-opacity="0.1"/><circle cx="32.2266" cy="32" r="25" fill="#A3CBFF" fill-opacity="0.3"/><circle cx="32.2266" cy="32" r="19" fill="#A3CBFF" fill-opacity="0.8"/><text x="32" y="38" text-anchor="middle" fill="#001F42" font-size="16px">{count}</text></svg>'

	const getIconContent = (svgStr) => {
		const newElement = document.createElement("div")
		newElement.innerHTML = svgStr
		return newElement
	}

	const clusterRenderer = {
		render: function ({ count, position }, stats, map) {
			const svg = clusterIcon.replace('{count}', count)
			const title = `${count} items`
			const zIndex = Number(window.google.maps.Marker.MAX_ZINDEX) + count

			const parser = new DOMParser()
			const svgEl = parser.parseFromString(svg, 'image/svg+xml').documentElement
			svgEl.setAttribute('transform', 'translate(0 25)')

			return new window.google.maps.marker.AdvancedMarkerElement({
				map,
				position,
				zIndex,
				title,
				content: svgEl
			})
		}
	};

	const initializeMap = (mapElement, setMapInstance, mapElementId) => {
		// filteredPins can still be the pre-fetch (pin-less) state on the first open,
		// because it is derived in a later effect; allPinsRef is already filled.
		const firstPin = [...filteredPins, ...(allPinsRef.current || [])].find(pin => pin.location?.lat && pin.location?.lng)
        const map = new window.google.maps.Map(mapElement, {
            zoom: 5,
            minZoom: 3,
            // maxZoom: 13,
            center: firstPin ? firstPin.location : DEFAULT_MAP_CENTER,
            disableDefaultUI: true,
            mapId: maps.mapId || 'mapId'
        });
        setMapInstance(map);
		
		if (mapElementId === 'map') {
			setMapMarkerClustererObjs([])
			setMapMarkerClusterer(new (window as any).markerClusterer.MarkerClusterer({ 
				markers: mapMarkerClustererObjs, 
				map,
				renderer: clusterRenderer
			}))
		} else if (mapElementId === 'full-map') {
			setFullMapMarkerClustererObjs([])
			setFullMapMarkerClusterer(new (window as any).markerClusterer.MarkerClusterer({ 
				markers: fullMapMarkerClustererObjs, 
				map,
				renderer: clusterRenderer
			}))
		}
    }

	const boundsChangedFn = (mapPins, mapElem, mapElemId, fpReference) => {
		const bounds = mapElem.getBounds()

		if (mapElemId == 'map' && (mapMarkerClustererObjs.length > 0)) {
			mapMarkerClusterer.removeMarkers(mapMarkerClustererObjs)
		} else if (mapElemId == 'full-map' && (fullMapMarkerClustererObjs.length > 0)) {
			fullMapMarkerClusterer.removeMarkers(fullMapMarkerClustererObjs)
		}
	
		mapPins.filter(pin => !pin.marker).forEach((pin) => {
			if (pin.location && pin.location.lat && pin.location.lng && bounds.contains(pin.location)) {
				const pinIncludedInFilter = fpReference.map(fp => fp.id).includes(pin.id)
				const marker = new window.google.maps.marker.AdvancedMarkerElement({
					position: pin.location,
					map: mapElem,
					title: pin.name,
					content: pinIncludedInFilter ? getIconContent(pin.isActive ? activeIcon : defaultIcon) : getIconContent('')
				});
				pin.marker = marker
				pin.isRendered = pinIncludedInFilter

				document.addEventListener('resetActivePin', (event: any) => {
					if (mapElemId !== event.detail?.mapId) {
						if ((mapElemId !== event.detail?.mapId) && event.detail?.isActive && (event.detail?.pinId == pin.id)) {
							pin.isActive = true
							pin.isRendered = true
							marker.content = getIconContent(activeIcon)
							mapElem.setCenter(pin.location)
						} else if (pin.isActive) {
							pin.isActive = false
							pin.isRendered = true
							marker.content = getIconContent(defaultIcon)
						}
					} else if ((event.detail?.pinId != pin.id) && pin.isRendered) {
						pin.isActive = false
						pin.isRendered = true
						marker.content = getIconContent(defaultIcon)
					}
				})

				document.addEventListener('onFilterPin', (event: any) => {
					const eventFilteredPins = event.detail?.filteredPins || []
					const firstPinLocation = event.detail?.firstPinLocation

					pin.isActive = false

					if (pin.marker) {
						if (eventFilteredPins.map(fp => fp.id).includes(pin.id)) {
							pin.isRendered = true
							marker.content = getIconContent(defaultIcon)
						} else {
							pin.isRendered = false
							marker.content = getIconContent('')
						}
					}

					if (firstPinLocation) {
						mapElem.setCenter(firstPinLocation)
					}

					window.google.maps.event.trigger(mapElem, 'bounds_changed')
				})

				// Add a click event listener to open the info window when the marker is clicked
				marker.addListener('click', function () {
					pin.isActive = !pin.isActive

					document.dispatchEvent(new CustomEvent('resetActivePin', { detail: { pinId: pin.id, isActive: pin.isActive, mapId: mapElemId, filteredPins: fpReference } }))

					setActivePartnerMap(allPartnersRef.current.find(p => p.id == pin.id))
					setShowPinInfo(pin.isActive)

					if (pin.isActive) {
						pin.isRendered = true
						marker.content = getIconContent(activeIcon)
					} else {
						pin.isRendered = true
						marker.content = getIconContent(defaultIcon)
					}
				});
			}
		});

		// Set the clusterer objects
		const markersToRenderer = mapPins.filter(pin => pin.marker && pin.location && pin.location.lat && pin.location.lng && bounds.contains(pin.location)).map(pin => pin.marker)
		if (mapElemId == 'map') {
			console.log('markersToRenderer', markersToRenderer)
			setMapMarkerClustererObjs(markersToRenderer)

			if (markersToRenderer.length > 0) {
				console.log('Add markers', mapMarkerClustererObjs)
				console.log('Marker', mapMarkerClusterer)
				mapMarkerClusterer.addMarkers(markersToRenderer)
			}
		} else if (mapElemId == 'full-map') {
			setFullMapMarkerClustererObjs(markersToRenderer)

			if (markersToRenderer.length > 0) {
				fullMapMarkerClusterer.addMarkers(markersToRenderer)
			}
		}
	}

	useEffect(() => {
		if(isMapAlreadyOpened) {
			const mapElement = document.getElementById('map');
			const fullMapElement = document.getElementById('full-map');
			if (mapElement && !mapInstance) {
				initializeMap(mapElement, setMapInstance, 'map');
			}
			if (fullMapElement && !fullMapInstance) {
				initializeMap(fullMapElement, setFullMapInstance, 'full-map');
			}
		}
    }, [isMapAlreadyOpened])

	useEffect(() => {
		if (mapInstance) {
			const mapPins = JSON.parse(JSON.stringify(allPinsRef.current || []));
			filteredPinsRef.current = filteredPins
	
			window.google.maps.event.addListener(mapInstance, 'bounds_changed', () => {
				boundsChangedFn(mapPins, mapInstance, 'map', filteredPinsRef.current)
			});

			document.addEventListener('resetActivePin', (event: any) => {
				if ((event.detail?.mapId !== 'map') && (event.detail?.isActive)) {
					const activePinLocation = mapPins.find(p => p.id === event.detail?.pinId)?.location
					
					if (activePinLocation) {
						mapPins.find(p => p.id === event.detail?.pinId).isActive = true
						mapInstance.setCenter(activePinLocation)
						window.google.maps.event.trigger(mapInstance, 'bounds_changed')
					}
				}
			})
		}
	}, [mapInstance])

	useEffect(() => {
		if (fullMapInstance) {
			const mapPins = JSON.parse(JSON.stringify(allPinsRef.current || []));
			filteredPinsRef.current = filteredPins
	
			window.google.maps.event.addListener(fullMapInstance, 'bounds_changed', () => {
				boundsChangedFn(mapPins, fullMapInstance, 'full-map', filteredPinsRef.current)
			});

			document.addEventListener('resetActivePin', (event: any) => {
				if ((event.detail?.mapId !== 'full-map') && (event.detail?.isActive)) {
					const activePinLocation = mapPins.find(p => p.id === event.detail?.pinId)?.location
					
					if (activePinLocation) {
						mapPins.find(p => p.id === event.detail?.pinId).isActive = true
						fullMapInstance.setCenter(activePinLocation)
						window.google.maps.event.trigger(fullMapInstance, 'bounds_changed')
					}
				}
			})
		}
	}, [fullMapInstance])

	useEffect(() => {
		const firstPinLocation = filteredPins[0]?.location
		filteredPinsRef.current = filteredPins
		document.dispatchEvent(new CustomEvent('onFilterPin', { detail: { filteredPins, firstPinLocation }}))
		setActivePartnerMap(null)
		setShowPinInfo(false)
    }, [filteredPins])

	const togglePinInfoMobile = () => {
		setShowPinInfo(false)
		setActivePartnerMap(null)

		document.dispatchEvent(new CustomEvent('resetActivePin', { detail: { filteredPins: filteredPins } }))
	}

	const openMap = () => {
		setIsMapOpen(true)
	}

	const closeMap = () => {
		setIsMapOpen(false)
	}

	// add 'no-scroll' class to body when the general information drawer is open
	// and enables the ability to close it by pressing the ESC key
	useEffect(() => {
		const handleEscape = (event: KeyboardEvent) => {
		   	if (event.key === 'Escape') {
				closeMap()
		   	}
		}

		if (isMapOpen) {
			document.addEventListener('keydown', handleEscape)
			document.body.classList.add('no-scroll')
			document.body.classList.add('map-open')
		} else {
			document.body.classList.remove('no-scroll')
			document.body.classList.remove('map-open')
		}
  
		return () => {
		   document.removeEventListener('keydown', handleEscape)
		}
	}, [isMapOpen])

	// bind a function on click to close the dropdown if clicked outside
	const operationsFilterRef = useRef(null)

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (operationsFilterRef.current && !operationsFilterRef.current.contains(event.target)) {
				setOperationsFilter(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	// bind a function on click to close the dropdown if clicked outside
	const productTypeFilterRef = useRef(null)

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (productTypeFilterRef.current && !productTypeFilterRef.current.contains(event.target)) {
				setProductTypeFilter(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const showMap = () => {
		if (isFullListLoadedRef.current) {
			setIsMapAlreadyOpened(true)
			return
		}

		loadFullList().then(loadedPartners => {
			if (!isFullListLoadedRef.current) {
				setHasMapLoadError(true)
				return
			}
			setHasMapLoadError(false)
			filterPartners(false, loadedPartners)
			setIsMapAlreadyOpened(true)
		})
	}

	useEffect(() => {
		if (isListMap == 'map') {
			showMap()
		}
	}, [isListMap])

	// Clicking Map again after a failed load is the retry.
	const selectMapView = () => {
		if (isListMap === 'map') {
			if (hasMapLoadError) showMap()
			return
		}
		setIsListMap('map')
	}

	const [operationsSelected, setOperationsSelected] = useState([])
	const [productTypeSelected, setProductTypeSelected] = useState([])

	const [searchText, setSearchText] = useState('')

	const handleSelectOperation = (item) => {
		setOperationsSelected((prev) => {
			if (item.title) {
				if (!prev.includes(item.title)) {
					return [...prev, item.title]
				} else {
					return prev.filter(operationSelected => operationSelected !== item.title)
				}
			}
		})
	}

	const handleSelectProductType = (item) => {
		setProductTypeSelected((prev) => {
			if (item.title) {
				if (!prev.includes(item.title)) {
					return [...prev, item.title]
				} else {
					return prev.filter(typeSelected => typeSelected !== item.title)
				}
			}
		})
	}

	const filterPartners = (shouldResetPage = true, sourcePartners?: any[]) => {
		const sourceList = sourcePartners || allPartnersRef.current
		let newFilteredPartners;
		
		if (searchText.trim() || operationsSelected.length > 0 || productTypeSelected.length > 0) {
			const lowercasedText = searchText.toLowerCase()
			newFilteredPartners = sourceList.filter(partner => {
				const filterFields = (partner.companyName || '') + 
					(partner.location?.country || '') + 
					(partner.location?.city || '') + 
					((partner.phones || []).map(p => p.phone).toString() || '') + 
					((partner.emails || []).map(e => e.email).toString() || '')

				const matchesSearch = filterFields.toLowerCase().includes(lowercasedText)
				const matchesOperations = operationsSelected.length === 0 || partner.operations.some((op) => operationsSelected.includes(op))
				const matchesProductTypes = productTypeSelected.length === 0 || partner.productTypes?.some((pt) => productTypeSelected.includes(pt))

				return matchesSearch && matchesOperations && matchesProductTypes
			})
		} else {
			newFilteredPartners = sourceList
		}

		setFilteredPartners(newFilteredPartners)
		if (shouldResetPage) setDisplayedItems(itemsPerPage)

		return newFilteredPartners
	}

	useEffect(() => {
		if (isParamsInitialized) {
			const debounceTimer = setTimeout(() => {
				loadFullList().then(loadedPartners => announceFilter(filterPartners(true, loadedPartners)))
	
				const currentUrl = new URL(window.location.href)
				const urlParams = new URLSearchParams(currentUrl.search)
				if (searchText) {
					urlParams.set('q', searchText)
				} else {
					urlParams.delete('q')
				}
				currentUrl.search = urlParams.toString()
				window.history.replaceState({}, '', currentUrl.href)
			}, 500)
			return () => clearTimeout(debounceTimer)
		}
	}, [searchText])

	useEffect(() => {
		if (isParamsInitialized) {
			loadFullList().then(loadedPartners => announceFilter(filterPartners(true, loadedPartners)))
	
			const currentUrl = new URL(window.location.href)
			const urlParams = new URLSearchParams(currentUrl.search)
			urlParams.delete('operation')
			if (operationsSelected && operationsSelected.length > 0) {
				operationsSelected.forEach(os => {
					urlParams.append('operation', os)
				})
			}

			currentUrl.search = urlParams.toString()
			window.history.replaceState({}, '', currentUrl.href)
		}
	}, [operationsSelected])

	useEffect(() => {
		if (isParamsInitialized) {
			loadFullList().then(loadedPartners => announceFilter(filterPartners(true, loadedPartners)))
	
			const currentUrl = new URL(window.location.href)
			const urlParams = new URLSearchParams(currentUrl.search)
			urlParams.delete('productType')
			if (productTypeSelected && productTypeSelected.length > 0) {
				productTypeSelected.forEach(pt => {
					urlParams.append('productType', pt)
				})
			}
	
			currentUrl.search = urlParams.toString()
			window.history.replaceState({}, '', currentUrl.href)
		}
	}, [productTypeSelected])
	
	useEffect(() => {
		setFilteredPins((prev) => {
			const filteredPartnersIds = filteredPartners.map(fp => fp.id)
			return (allPinsRef.current || []).filter(p => filteredPartnersIds.includes(p.id))
		})
	}, [filteredPartners])

	useEffect(() => {
		const currentUrl = new URL(window.location.href)
		const urlParams = new URLSearchParams(currentUrl.search)
		const operationsParam = urlParams.getAll('operation') || []
		const productTypeParam = urlParams.getAll('productType') || []
		const searchParam = urlParams.get('q') || ''

		if (operationsParam.length > 0) {
			setOperationsSelected(operationsParam)
		}
		if (productTypeParam.length > 0) {
			setProductTypeSelected(productTypeParam)
		}
		if (searchParam) {
			setSearchText(searchParam)
		}

		// filter the partners
		filterPartners(false)

		setIsParamsInitialized(true)
	}, [])

	// marks the first partner of the batch when the control will not survive it,
	// so focus does not fall to <body>
	const afterLoadMore = (list: any[], firstNewIndex: number, shown: number) => {
		if (shown >= list.length) focusIndexRef.current = firstNewIndex
		announceResults(list, shown)
	}

	const finishLoadingMore = () => {
		isLoadingMoreRef.current = false
		setIsLoadingMore(false)
	}

	const loadMoreItems = () => {
		if (isLoadingMoreRef.current) return
		isLoadingMoreRef.current = true
		setIsLoadingMore(true)

		if (!isFullListLoadedRef.current) {
			loadFullList().then(loadedPartners => {
				// the server renders every partner on its own, so fall back to the link
				if (!isFullListLoadedRef.current && allPartnersUrl) {
					window.location.href = allPartnersUrl
					finishLoadingMore()
					return
				}

				const filtered = filterPartners(false, loadedPartners)
				const shown = Math.min(displayedItems + itemsPerPage, filtered.length)

				setDisplayedItems(prev => prev + itemsPerPage)
				finishLoadingMore()
				afterLoadMore(filtered, displayedItems, shown)
			})
			return
		}

		setTimeout(() => {
			const shown = Math.min(displayedItems + resultsPerPage, filteredPartners.length)

			setDisplayedItems(prev => prev + resultsPerPage)
			finishLoadingMore()
			afterLoadMore(filteredPartners, displayedItems, shown)
		}, 300) // Small delay to show loading state
	}

	useEffect(() => {
		const index = focusIndexRef.current
		if (index === null) return
		focusIndexRef.current = null

		const firstNewCard = listRef.current?.children[index] as HTMLElement
		firstNewCard?.querySelector<HTMLElement>('a, button')?.focus()
	}, [displayedItems, filteredPartners])

	// One anchor carries both behaviours (JOT-283 (a)): clicking it loads the next
	// page in place, while its href stays a real link to the full list, so a crawler
	// or anyone opening it in a new tab can still reach every partner.
	const hasModifier = (event) => event.metaKey || event.ctrlKey || event.shiftKey || event.altKey

	const activateLoadMore = (event) => {
		if (hasModifier(event)) return

		event.preventDefault()
		loadMoreItems()
	}

	// An anchor is not activated by Space the way a button is; restore it.
	// Enter is native on an anchor, so it is only handled when there is no href.
	const handleLoadMoreKeyDown = (event) => {
		const isSpace = event.key === ' ' || event.key === 'Spacebar'
		if (!isSpace && !(event.key === 'Enter' && !allPartnersUrl)) return
		if (hasModifier(event)) return

		event.preventDefault()
		if (event.repeat) return
		loadMoreItems()
	}

	// Before the full list arrives there are more partners on the server than in
	// filteredPartners, so the server count decides whether the control shows.
	const knownPartnersCount = isFullListLoaded
		? filteredPartners.length
		: Math.max(filteredPartners.length, totalPartners || 0)

	const scrollToContent = () => {
		setTimeout(() => {
			contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
		}, 100)
	}

	return (
		<>
			<div className={clsx('map-fullscreen', isMapOpen && 'map-fullscreen--active')}>
				
				<div className='map-fullscreen__map'>

					<div id="full-map"></div>

					<button
						className='close-map-fullscreen'
						onClick={closeMap}
						aria-label='Close map'
					>
						<Contract />
					</button>

				</div>

				<div className='map-fullscreen__wrapper' tabIndex={fakeFullscreenMap ? -1 : null}>

					<div className={clsx(
						'pin-selected',
						showPinInfo && 'active'
					)}>
						{showPinInfo && (
							<>

								<button
									onClick={togglePinInfoMobile}
									className='close-pin'
								>
									<Close />
								</button>

								{showPinInfo && activePartnerMap &&
									<MaritimePartnerCard
										isAlwaysOpen={true}
										companyName={activePartnerMap.companyName}
										location={activePartnerMap.location}
										phones={activePartnerMap.phones}
										emails={activePartnerMap.emails}
										website={activePartnerMap.website}
										operations={activePartnerMap.operations}
									/>
								}

							</>
						)}
					</div>

					<div className={clsx(
						'no-pin-selected',
						!showPinInfo && 'active'
					)}>

						<p>
							{mapPlaceholder}
						</p>

					</div>

				</div>

			</div>

			{topPart && (
				<Section
					bgColor='bg-beige-050'
					textColor='text-dark'
					className='top-section'
				>
					<div className='grid-container'>
						<div className='grid-row'>
							<div className='c-12 c-md-10 c-lg-8'>
								<div className='top-section__wrapper'>

									{isNonWhitespaceString(topPart?.title) && (
										<h1 className='title-lg top-section__title'>
											{topPart?.title}
										</h1>
									)}

									{isNonWhitespaceString(topPart?.intro) && (
										<div className='top-section__intro'>
											<RichText content={topPart?.intro} />
										</div>
									)}

								</div>
							</div>
						</div>
					</div>
				</Section>
			)}

			<Section
				bgColor='bg-beige-050'
				textColor='text-dark'
				className={clsx(
					'top-filters',
					!(topPart?.title || topPart?.intro) && 'top-filters--no-title'
				)}
			>
				<div className='grid-container'>
					<div className='top-filters__wrapper'>

						<div className='filters-left'>

							<div className='search'>

								<button className='icon' aria-label='Search'>
									<svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M8.06253 2.5C4.99045 2.5 2.50003 4.99042 2.50003 8.0625C2.50003 11.1346 4.99045 13.625 8.06253 13.625C11.1346 13.625 13.625 11.1346 13.625 8.0625C13.625 4.99042 11.1346 2.5 8.06253 2.5ZM0.500031 8.0625C0.500031 3.88585 3.88588 0.5 8.06253 0.5C12.2392 0.5 15.625 3.88585 15.625 8.0625C15.625 12.2392 12.2392 15.625 8.06253 15.625C3.88588 15.625 0.500031 12.2392 0.500031 8.0625Z M12.7024 11.2882L17.9135 16.4993L16.4993 17.9136L11.2881 12.7024L12.7024 11.2882Z' fill='currentColor'/></svg>
								</button>

								<input
									type='text'
									className='input'
									value={searchText || ''}
									placeholder={searchPlaceholder || 'Search by company name or country'}
									onChange={(e) => setSearchText(e.target.value)}
								/>

							</div>

							<div className='selects'>

								<div
									className={clsx(
										'select',
										productTypeFilter && 'open'
									)}
									ref={productTypeFilterRef}
								>
									
									<button
										className='select-title'
										onClick={() => setProductTypeFilter(!productTypeFilter)}
									>

										<span className='text'>
											{productType?.title}
										</span>

										<span className='icon'>
											<AngleDown />
										</span>

									</button>

									<div className='select-wrapper'>
										<div>
											<div>
												{productType?.items.map((item, i) => (
													<Checkbox 
														key={i} 
														onChange={() => handleSelectProductType(item)} 
														checked={productTypeSelected.includes(item.title)}
													>
														{item?.title}
													</Checkbox>
												))}
											</div>
										</div>
									</div>

								</div>

								<div
									className={clsx(
										'select',
										operationsFilter && 'open'
									)}
									ref={operationsFilterRef}
								>
									
									<button
										className='select-title'
										onClick={() => setOperationsFilter(!operationsFilter)}
									>

										<span className='text'>
											{operations?.title}
										</span>

										<span className='icon'>
											<AngleDown />
										</span>

									</button>

									<div className='select-wrapper'>
										<div>
											<div>
												{operations?.items.map((item, i) => (
													<Checkbox key={i} onChange={() => handleSelectOperation(item)} checked={operationsSelected.includes(item.title)}>
														{item?.title}
													</Checkbox>
												))}
											</div>
										</div>
									</div>

								</div>

							</div>

						</div>

						<div className='filters-right'>

							<p className='label'>
								{tabsText.headerText}
							</p>

							<div className='toggle-switch'>

								<button
									className={isListMap === 'list' ? 'active' : null}
									onClick={() => setIsListMap('list')}
								>
									{tabsText.listText}
								</button>

								<button
									className={isListMap === 'map' ? 'active' : null}
									onClick={selectMapView}
								>
									{tabsText.mapText}
								</button>

							</div>

						</div>
						
					</div>
				</div>
			</Section>

			<Section
				bgColor='bg-beige-050'
				textColor='text-dark'
				className='middle-section'
			>
				<div className='grid-container'>
					<div className='middle-section__wrapper'>
					
						<div className='middle-section__sidebar'>

							<div className='checkboxes'>

								<p className='checkboxes-label'>
									{productType?.title}
								</p>

								{productType?.items.map((item, i) => (
									<Checkbox 
										key={i} 
										onChange={() => handleSelectProductType(item)} 
										checked={productTypeSelected.includes(item.title)}
									>
										{item?.title}
									</Checkbox>
								))}

							</div>

							<div className='checkboxes'>

								<p className='checkboxes-label'>
									{operations?.title}
								</p>

								{operations?.items.map((item, i) => (
									<Checkbox key={i} onChange={() => handleSelectOperation(item)} checked={operationsSelected.includes(item.title)}>
										{item?.title}
									</Checkbox>
								))}

							</div>

						</div>

						<div className='middle-section__content'>

							<div ref={contentRef} className='content-ref'></div>

							<Announcer message={announcement} />

							<div
								ref={listRef}
								className={clsx('content-list', isListMap === 'list' && 'content-list--active')}
								aria-busy={isLoadingMore ? true : undefined}
							>

								{filteredPartners && Array.isArray(filteredPartners) && filteredPartners.length > 0 ? (
									<>
										{filteredPartners.slice(0, displayedItems).map((item, i) => (
											<MaritimePartnerCard
												key={i}
												isAlwaysOpen={false}
												companyName={item.companyName}
												location={item.location}
												phones={item.phones}
												emails={item.emails}
												website={item.website}
												operations={item.operations}
											/>
										))}
									</>
								) : (
									<div className='content-list__no-results'>
										<p className='text-lg'>
											{noResultsMessage}
										</p>
									</div>
								)}
								
							</div>

							<div className={clsx('content-map', isListMap === 'map' && 'content-map--active')}>

								{hasMapLoadError ? (
									<div className='content-list__no-results'>
										<p className='text-lg'>
											{noResultsMessage}
										</p>
									</div>
								) : (
									<div className='map-wrapper'>

										<div id="map"></div>

										<button
											className='open-map-fullscreen'
											onClick={openMap}
											aria-label='Open map'
										>
											<Expand />
										</button>

									</div>
								)}

								{showPinInfo && (
									<div className='map-location-details'>
										{activePartnerMap &&
											<MaritimePartnerCard
												isAlwaysOpen={true}
												companyName={activePartnerMap.companyName}
												location={activePartnerMap.location}
												phones={activePartnerMap.phones}
												emails={activePartnerMap.emails}
												website={activePartnerMap.website}
												operations={activePartnerMap.operations}
											/>
										}
									</div>
								)}

							</div>

							{isListMap === 'list' && knownPartnersCount > displayedItems && (
								<div className='content-list__pagination'>
									<a
										href={allPartnersUrl || undefined}
										role='button'
										tabIndex={allPartnersUrl ? undefined : 0}
										onClick={activateLoadMore}
										onKeyDown={handleLoadMoreKeyDown}
										aria-busy={isLoadingMore ? true : undefined}
										aria-disabled={isLoadingMore ? true : undefined}
										className='load-more'
									>
										{isLoadingMore ? (
											loading
										) : (
											<>
												Load More <svg width='14' height='14' fill='currentColor' viewBox='0 0 14 14' xmlns='http://www.w3.org/2000/svg'><path d='M7.6998 0.800049V6.30005H13.1998V7.70005H7.6998V13.2H6.2998V7.70005H0.799805V6.30005H6.2998V0.800049H7.6998Z' /></svg>
											</>
										)}
									</a>
								</div>
							)}

						</div>

					</div>
				</div>
			</Section>
		</>
	)
}
